import { apiGet, apiPost, isApiConfigured } from "@/services/http";

export type ShareEntityType = "business" | "spot";

export interface TopSharedRow {
  entity_slug: string;
  entity_name: string | null;
  entity_type: string;
  shares: number;
}

/**
 * Records a single share / copy interaction against the project's own
 * Node + MariaDB backend (VITE_PUBLIC_API_URL). Fire-and-forget: failures are
 * swallowed so the UI is never blocked by analytics.
 */
export async function trackShare(
  entityType: ShareEntityType,
  slug: string,
  name: string,
  platform: string,
): Promise<void> {
  if (!isApiConfigured() || !slug) return;
  try {
    await apiPost("/shares", {
      entityType,
      entitySlug: slug,
      entityName: name,
      platform,
    });
  } catch {
    // Analytics only — never surface an error to the visitor.
  }
}

/**
 * Returns the most-shared listings / spots, aggregated by slug.
 * In demo mode (no live API) it resolves to an empty list so the caller
 * can show its own sample data.
 */
export async function getTopShared(limit = 10): Promise<TopSharedRow[]> {
  if (!isApiConfigured()) return [];
  return apiGet<TopSharedRow[]>(`/shares/top?limit=${limit}`);
}

/** Stable, slug-derived sample count so demo mode still looks populated. */
function demoShareCount(slug: string): number {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) {
    hash = (hash * 31 + slug.charCodeAt(i)) % 997;
  }
  return 6 + (hash % 45);
}

/**
 * Total number of times a single listing / spot has been shared.
 * Paid-only feature; falls back to a demo value when no live API is set.
 */
export async function getShareCount(entityType: ShareEntityType, slug: string): Promise<number> {
  if (!slug) return 0;
  if (!isApiConfigured()) return demoShareCount(slug);
  const row = await apiGet<{ shares: number }>(
    `/shares/count?type=${entityType}&slug=${encodeURIComponent(slug)}`,
  );
  return row?.shares ?? 0;
}